import { useRef, useState } from "react";
import { useApiClient } from "../api/ApiClientContext";
import { uploadReleaseImage } from "../api/catalog-api";
import { userFacingMessage } from "../api/errors";
import type { PublicRelease } from "../api/types";
import { Button } from "./Button";

type ReleasePhotoUploadProps = {
  release: PublicRelease;
  onUploaded: (release: PublicRelease, uploadedImageId: string | null) => void;
};

export function ReleasePhotoUpload({
  release,
  onUploaded,
}: ReleasePhotoUploadProps) {
  const client = useApiClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasImage = Boolean(release.primaryImage || (release.images && release.images.length > 0));

  function clearSelection() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(null);
    setPreviewUrl(null);
    setError(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }

  function chooseFile(next: File | null) {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setFile(next);
    setPreviewUrl(next ? URL.createObjectURL(next) : null);
    setError(null);
  }

  async function upload() {
    if (!file || busy) {
      return;
    }
    setBusy(true);
    setError(null);
    const previousIds = new Set((release.images ?? []).map((image) => image.imageId));
    try {
      const updated = await uploadReleaseImage(client, release.releaseId, file);
      const uploaded =
        updated.images?.find((image) => !previousIds.has(image.imageId)) ??
        updated.images?.find((image) => image.source === "USER_UPLOAD" && image.ownedByCurrentUser) ??
        null;
      clearSelection();
      onUploaded(updated, uploaded?.imageId ?? null);
    } catch (err) {
      setError(userFacingMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="hw-photo-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
        hidden
        onChange={(event) => {
          chooseFile(event.target.files?.[0] ?? null);
        }}
      />

      {!file ? (
        <Button
          type="button"
          variant={hasImage ? "ghost" : "primary"}
          className={hasImage ? "hw-photo-upload__secondary" : "hw-photo-upload__primary"}
          onClick={() => inputRef.current?.click()}
        >
          {hasImage ? "Добавить ещё фото" : "Добавить фото"}
        </Button>
      ) : (
        <div className="hw-photo-upload__preview-card">
          {previewUrl ? (
            <img src={previewUrl} alt="Предпросмотр" className="hw-photo-upload__preview" />
          ) : (
            <p className="hw-photo-upload__filename">{file.name}</p>
          )}
          <div className="hw-photo-upload__actions">
            <Button type="button" onClick={() => void upload()} disabled={busy}>
              {busy ? "Загрузка…" : "Загрузить"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              Выбрать другое
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={clearSelection}>
              Отмена
            </Button>
          </div>
        </div>
      )}

      {error ? <p className="hw-photo-upload__error">{error}</p> : null}
    </div>
  );
}
